import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppSelectFilter from "../components/common/AppSelectFilter";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import ClassroomSelectFilter from "../components/common/ClassroomSelectFilter";
import AcademicYearSelectFilter from "../components/common/AcademicYearSelectFilter";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";
import ConfirmDialog from "../components/common/ConfirmDialog";
import RowActionMenu, {
  RowActionDef,
} from "../components/common/RowActionMenu";

import CreateEnrollmentModal from "../components/enrollments/CreateEnrollmentModal";
import UpdateEnrollmentModal from "../components/enrollments/UpdateEnrollmentModal";
import ViewEnrollmentModal from "../components/enrollments/ViewEnrollmentModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getEnrollments,
  getEnrollmentById,
  deleteEnrollment,
  resetEnrollmentServerStatus,
} from "../redux/actions/enrollmentActions";
import { ENROLLMENT_STATUS_OPTIONS } from "../shared/data/enumOptions";
import { getClassrooms } from "../redux/actions/classroomActions";
import { getAcademicYears } from "../redux/actions/academicYearActions";
import { EnrollmentResponseDto } from "../shared/dtos/enrollment/EnrollmentResponseDto";
import { EnrollmentStatus } from "../shared/enums/EnrollmentStatus";
import { formatDateForDisplay } from "../helpers/dateHelpers";

const EnrollmentsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: enrollments,
    item: enrollment,
    loadingFetchAll: loadingEnrollments,
    loadingFetchOne: loadingEnrollment,
    errorFetchAll: errorLoadingEnrollments,
    errorFetchOne: errorLoadingEnrollment,
    pagination,
    serverStatus,
    loadingCreate: loadingEnrollmentCreate,
    errorCreate: errorCreatingEnrollment,
    loadingUpdate: loadingEnrollmentUpdate,
    errorUpdate: errorUpdatingEnrollment,
    loadingDelete: loadingEnrollmentDelete,
  } = useAppSelector((state) => state.enrollment);

  const { items: classrooms } = useAppSelector((state) => state.classroom);
  const { items: academicYears } = useAppSelector(
    (state) => state.academicYear,
  );

  // Filters State
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce(searchTerm, 500);

  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedClassroomId, setSelectedClassroomId] = useState<string>("");
  const [selectedAcademicYearId, setSelectedAcademicYearId] =
    useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [selectedEnrollment, setSelectedEnrollment] =
    useState<EnrollmentResponseDto | null>(null);

  const canCreate = hasPermission("enrollment:create");
  const canUpdate = hasPermission("enrollment:update");
  const canDelete = hasPermission("enrollment:delete");
  const canRead = hasPermission("enrollment:read");

  useEffect(() => {
    if (!classrooms.length) dispatch(getClassrooms());
    if (!academicYears.length) dispatch(getAcademicYears());
  }, [dispatch, classrooms.length, academicYears.length]);

  const fetchEnrollmentsList = useCallback((): void => {
    dispatch(
      getEnrollments({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch.trim() || undefined,
        branchId: selectedBranchId || undefined,
        classroomId: selectedClassroomId || undefined,
        academicYearId: selectedAcademicYearId || undefined,
        status: (selectedStatus as EnrollmentStatus) || undefined,
      }),
    );
  }, [
    dispatch,
    pageConfig,
    debouncedSearch,
    selectedBranchId,
    selectedClassroomId,
    selectedAcademicYearId,
    selectedStatus,
  ]);

  useEffect(() => {
    fetchEnrollmentsList();
  }, [fetchEnrollmentsList]);

  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingEnrollmentCreate) setIsCreateModalOpen(false);
  }, [loadingEnrollmentCreate]);

  const handleOpenUpdate = (row: EnrollmentResponseDto): void => {
    setSelectedEnrollment(row);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingEnrollmentUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedEnrollment(null);
    }
  }, [loadingEnrollmentUpdate]);

  const handleOpenDelete = (row: EnrollmentResponseDto): void => {
    setSelectedEnrollment(row);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingEnrollmentDelete) {
      setIsDeleteModalOpen(false);
      setSelectedEnrollment(null);
    }
  }, [loadingEnrollmentDelete]);

  const handleOpenView = (row: EnrollmentResponseDto): void => {
    dispatch(getEnrollmentById(row.id));
    setIsViewModalOpen(true);
  };
  const handleCloseView = (): void => setIsViewModalOpen(false);

  const handleDeleteConfirm = (): void => {
    if (selectedEnrollment) {
      dispatch(deleteEnrollment(selectedEnrollment.id));
    }
  };

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetEnrollmentServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const columns = useMemo<TableColumn<EnrollmentResponseDto>[]>(
    () => [
      { id: "admissionNumber", label: "Admission No.", minWidth: 120 },
      {
        id: "studentName",
        label: "Student Name",
        minWidth: 170,
        format: (value) => (
          <Typography
            component="span"
            sx={{ fontWeight: 600, fontSize: "0.875rem" }}
          >
            {String(value || "N/A")}
          </Typography>
        ),
      },
      { id: "classroomName", label: "Classroom", minWidth: 130 },
      { id: "academicYearName", label: "Academic Year", minWidth: 120 },
      {
        id: "status",
        label: "Status",
        minWidth: 120,
        format: (value) => <StatusChip status={value as any} />,
      },
      {
        id: "enrollmentDate",
        label: "Enrolled On",
        minWidth: 120,
        format: (value) => formatDateForDisplay(value as string),
      },
    ],
    [],
  );

  const renderRowActions = (row: EnrollmentResponseDto): React.ReactNode => {
    const inlineActions: RowActionDef[] = [
      {
        label: "View",
        icon: <VisibilityOutlinedIcon fontSize="small" />,
        color: "info",
        onClick: () => handleOpenView(row),
        show: canRead,
      },
    ];

    const menuActions: RowActionDef[] = [
      {
        label: "Edit Enrollment",
        icon: <EditOutlinedIcon fontSize="small" />,
        onClick: () => handleOpenUpdate(row),
        show: canUpdate,
      },
      {
        label: "Delete Enrollment",
        icon: <DeleteOutlineIcon fontSize="small" />,
        color: "error",
        onClick: () => handleOpenDelete(row),
        show: canDelete,
      },
    ];

    return (
      <RowActionMenu inlineActions={inlineActions} menuActions={menuActions} />
    );
  };

  return (
    <PageContainer
      title="Enrollment Management"
      subtitle="Manage student classroom allocations and academic status tracking."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            New Enrollment
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPageConfig((prev) => ({ ...prev, page: 1 }));
        }}
        searchPlaceholder="Search by student or admission..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={(bId) => {
            setSelectedBranchId(bId);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
        />

        <ClassroomSelectFilter
          value={selectedClassroomId}
          onChange={setSelectedClassroomId}
        />
        <AcademicYearSelectFilter
          value={selectedAcademicYearId}
          onChange={setSelectedAcademicYearId}
        />
        <AppSelectFilter
          label="Status"
          value={selectedStatus}
          onChange={setSelectedStatus}
          options={ENROLLMENT_STATUS_OPTIONS}
          allLabel="All Statuses"
        />
      </PageToolbar>

      <StandardTable<EnrollmentResponseDto>
        columns={columns}
        data={enrollments}
        loading={loadingEnrollments}
        error={errorLoadingEnrollments}
        onRetry={fetchEnrollmentsList}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(newPage) =>
          setPageConfig((prev) => ({ ...prev, page: newPage }))
        }
        onRowsPerPageChange={(newLimit) =>
          setPageConfig((prev) => ({ ...prev, limit: newLimit, page: 1 }))
        }
        rowAction={renderRowActions}
      />

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateEnrollmentModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingEnrollmentCreate}
          error={errorCreatingEnrollment}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && selectedEnrollment && (
        <UpdateEnrollmentModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          enrollment={selectedEnrollment}
          loading={loadingEnrollmentUpdate}
          error={errorUpdatingEnrollment}
        />
      )}

      {/* VIEW MODAL */}
      {isViewModalOpen && (
        <ViewEnrollmentModal
          open={isViewModalOpen}
          onClose={handleCloseView}
          enrollment={enrollment}
          loading={loadingEnrollment}
          error={errorLoadingEnrollment}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteModalOpen && selectedEnrollment && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Enrollment"
          description={`Are you sure you want to delete the enrollment for "${selectedEnrollment.studentName}"? This action cannot be undone.`}
          loading={loadingEnrollmentDelete}
        />
      )}
    </PageContainer>
  );
};

export default EnrollmentsPage;
